import React from 'react';
import { useIntl } from 'react-intl';
import { Button, Icon } from 'components/ui';
import classNames, { Argument } from 'classnames';
import SignatureCanvas from 'react-signature-canvas';
import { ButtonType } from 'components/ui/Button';
interface SignatureInputProps {
  onChange: (signature?: HTMLCanvasElement) => void;
  'data-testid'?: string;
  className?: Argument;
}

const SignatureInput: React.FC<SignatureInputProps> = ({ onChange, 'data-testid': testId, className }) => {
  const intl = useIntl();
  const signatureRef = React.useRef<SignatureCanvas>(null);
  const observerRef = React.useRef<ResizeObserver>(null);

  const handleSignatureChange = React.useCallback(() => {
    if (signatureRef.current) {
      onChange(signatureRef.current.getTrimmedCanvas());
    }
  }, [onChange]);

  const handleReset = React.useCallback(() => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      onChange();
    }
  }, [onChange]);

  // SEE: https://github.com/agilgur5/react-signature-canvas/issues/44
  const measuredRef = React.useCallback((node: SignatureCanvas) => {
    signatureRef.current = node;
    if (signatureRef.current) {
      observerRef.current?.disconnect();
      const canvas = signatureRef.current.getCanvas();

      if (canvas) {
        observerRef.current = new ResizeObserver(() => {
          if (signatureRef.current && canvas?.parentElement) {
            const signature = signatureRef.current.toData();

            canvas.height = canvas.parentElement.clientHeight;
            canvas.width = canvas.parentElement.clientWidth;

            signatureRef.current.fromData(signature);
          }
        });

        observerRef.current.observe(canvas);
      }
    }
  }, []);

  return (
    <div
      data-testid={testId}
      className={classNames(
        'relative rounded-card h-80 kiosk:h-192 w-full border-2 border-dashed border-gray-200 overflow-hidden signature',
        className,
      )}
    >
      <SignatureCanvas
        ref={measuredRef}
        clearOnResize={false}
        onEnd={handleSignatureChange}
        canvasProps={{ className: 'h-full w-full bg-gray-100' }}
      />
      <Button
        type={ButtonType.ICON}
        className="absolute right-0 bottom-0"
        onClick={handleReset}
        aria-label={intl.formatMessage({ id: 'accessibility.button.deleteSignature', defaultMessage: 'Delete signature' })}>
        <Icon className="fb-icon--close text-2xl kiosk:text-4xl text-default flex items-center justify-center absolute rounded-full bg-white right-0 bottom-0 m-2 kiosk:m-4 size-8 kiosk:size-16" />
      </Button>
    </div>
  );
};

export default SignatureInput;
